import React from 'react';
import { Helmet } from 'react-helmet-async';

import { usePagesApi } from './api/usePagesApi';
import { useProductsApi } from './api/useProductsApi';
import { useAppContext } from './AppContext';
import { env } from './env';
import { t } from './i18n/i18n';
import { useHref } from './router/router';
import { getAbsoluteUrl, getDefaultLocale, getLocalizedAbsoluteUrl, isFriendlySearch } from './util';

export interface MainHelmetProps {
  faviconIco?: string;
  faviconPng?: string;
}
export const MainHelmet = ({ faviconIco, faviconPng }: MainHelmetProps) => {
  const [title, setTitle] = React.useState<string>(t('misc.storeName'));
  const [description, setDescription] = React.useState<string>();
  const [pathsInAllLocales, setPathsInAllLocales] = React.useState<Partial<Record<string, string>>>();

  const { getPageMetaByPath } = usePagesApi();
  const { hasMultipleLocalesOrShippingCountries, info, locale, routeInfo } = useAppContext();
  const { getPathsInAllLocalesByPath } = usePagesApi();
  const { getUrlsInAllLocalesByPath } = useProductsApi();

  /*
    Al cambio della path chiedo i meta dati
  */
  React.useEffect(() => {
    routeInfo?.location.pathname &&
      routeInfo.dynamicSegment === false &&
      getPageMetaByPath(routeInfo.location.pathname).then((response) => {
        setTitle(response.data?.title ?? t('misc.storeName'));
        setDescription(response.data?.description);
      });
  }, [getPageMetaByPath, routeInfo?.dynamicSegment, routeInfo?.location.pathname]);

  /*
    Al cambio della path chiedo le url nelle altre linge, per l'hreflang
  */
  React.useEffect(() => {
    if (hasMultipleLocalesOrShippingCountries) {
      if (routeInfo?.location.pathname && routeInfo.dynamicSegment === false) {
        if (routeInfo.type === 'product') {
          getUrlsInAllLocalesByPath(routeInfo.location.pathname, { suppressErrorModal: true })
            .then((response) => {
              setPathsInAllLocales(response.data);
            })
            .catch(() => {
              // pazienza
            });
        } else if (
          routeInfo.type === 'catalog' ||
          routeInfo.type === 'code' ||
          routeInfo.type === 'template' ||
          routeInfo.type === 'route'
        ) {
          getPathsInAllLocalesByPath(routeInfo.location.pathname, { suppressErrorModal: true })
            .then((response) => {
              setPathsInAllLocales(response.data);
            })
            .catch(() => {
              // pazienza
            });
        } else if (routeInfo.type === 'fixed') {
          setPathsInAllLocales(
            info?.supportedLocales
              ? info.supportedLocales.reduce<Record<string, string>>((prev, curr) => {
                  prev[curr.code] = routeInfo.location.pathname;
                  return prev;
                }, {})
              : {},
          );
        }
      }
    }
  }, [
    getPathsInAllLocalesByPath,
    getUrlsInAllLocalesByPath,
    info?.supportedLocales,
    routeInfo?.dynamicSegment,
    routeInfo?.location.pathname,
    routeInfo?.type,
    hasMultipleLocalesOrShippingCountries,
  ]);

  const isPaginatedSearch: boolean = routeInfo?.location.search.includes('pageNum') ?? false;
  const hasIdStoreSku: boolean = routeInfo?.location.search.includes('idStoreSku') ?? false;
  /*
    Ricerca interna non friendly (/search, con simpleSearch e/o filtri).
    Il canonical deve essere self-referential: pagine con parametri diversi mostrano risultati diversi,
    non sono duplicati della /search generica, quindi dichiararle tali è un segnale scorretto che
    Google scarta. L'esclusione dall'indice è affidata al noindex, non al canonical.
  */
  const isInternalSearch: boolean = routeInfo !== undefined && !isFriendlySearch(routeInfo.location);
  const canonicalHref = useHref(
    (isPaginatedSearch || isInternalSearch) && routeInfo?.location
      ? { pathname: routeInfo.location.pathname, search: routeInfo.location.search }
      : (routeInfo?.location.pathname ?? ''),
  );

  return (
    <>
      {locale && (
        <Helmet>
          <title>{title}</title>
          {description && <meta content={description} name="description" />}
          {faviconIco && (
            <link href={faviconIco.startsWith('http') ? faviconIco : `${env.PUBLIC_URL}/${faviconIco}`} rel="icon" />
          )}
          {faviconPng && (
            <link
              href={faviconPng.startsWith('http') ? faviconPng : `${env.PUBLIC_URL}/${faviconPng}`}
              rel="apple-touch-icon"
            />
          )}
          {info?.shippingCountriesLocales &&
            Object.entries(info.shippingCountriesLocales).map(([_shippingCountry, shippingCountryLocales]) =>
              shippingCountryLocales?.map(
                (shippingCountryLocale) =>
                  pathsInAllLocales && (
                    <link
                      href={getLocalizedAbsoluteUrl(
                        shippingCountryLocale.code,
                        _shippingCountry,
                        pathsInAllLocales[shippingCountryLocale.code],
                      )}
                      hrefLang={`${shippingCountryLocale.code}-${_shippingCountry.toLowerCase()}`}
                      key={`${shippingCountryLocale.code}-${_shippingCountry.toLowerCase()}`}
                      rel="alternate"
                    />
                  ),
              ),
            )}

          {pathsInAllLocales && info?.defaultShippingCountry && (
            <link
              href={getLocalizedAbsoluteUrl(
                getDefaultLocale(info).code,
                info.defaultShippingCountry.code,
                pathsInAllLocales[getDefaultLocale(info).code],
              )}
              hrefLang="x-default"
              rel="alternate"
            />
          )}
          {!hasIdStoreSku && <link href={getAbsoluteUrl(canonicalHref)} rel="canonical" />}
          {/*
            Le pagine di ricerca interna sono thin content e non vanno indicizzate, ma i link ai
            prodotti vanno seguiti: noindex,follow. Il canonical da solo non basta, è un suggerimento
            che Google può ignorare, mentre il noindex è una direttiva.
            Non agganciare la condizione alla presenza di query param: le pagine categoria filtrate
            (type catalog / listing) hanno anch'esse parametri e devono restare indicizzabili.
            TODO valutare l'estensione ad altre pagine non indicizzabili (carrello, checkout, area account).
          */}
          {isInternalSearch && <meta content="noindex,follow" name="robots" />}
        </Helmet>
      )}
    </>
  );
};
